import * as React from 'react';
import { useTheme } from '@emotion/react';
import keyMirror from 'keymirror';
import { FaUmbrellaBeach, FaCubes } from 'react-icons/fa';
import { FiGift, FiSun, FiClock, FiStar } from 'react-icons/fi';

const PRODUCT_TAG = keyMirror({
  OUTSIDE: null,
  INCLUDES_ICE: null,
  GIFT: null,
  SUMMER_SPECIAL: null,
  SPECIAL: null,
  LIMITED_PERIOD: null,
});

const TAG_CONTENT = {
  [PRODUCT_TAG.OUTSIDE]: {
    IconComponent: FaUmbrellaBeach,
    label: 'Für dusse geignet',
  },
  [PRODUCT_TAG.INCLUDES_ICE]: {
    IconComponent: FaCubes,
    label: 'Ice isch debi',
  },
  [PRODUCT_TAG.GIFT]: {
    IconComponent: FiGift,
    label: 'Als Gschänk geignet',
  },
  [PRODUCT_TAG.SUMMER_SPECIAL]: {
    IconComponent: FiSun,
    label: 'Summer Special',
  },
  [PRODUCT_TAG.SPECIAL]: {
    IconComponent: FiStar,
    label: 'Special',
  },
  [PRODUCT_TAG.LIMITED_PERIOD]: {
    IconComponent: FiClock,
    label: 'Nur für churzi Ziit',
  },
};

function ProductLabel({
  initialValue,
  productLabel,
  shrink,
  size,
  color,
  className,
}) {
  const theme = useTheme();
  const [showText, setShowText] = React.useState(true);

  const content = React.useMemo(() => TAG_CONTENT[productLabel], [
    productLabel,
  ]);

  const height = React.useMemo(() => (size === 'small' ? 4 : 5));
  const fontSize = React.useMemo(() =>
    size === 'small' ? '0.6rem' : '0.8rem'
  );
  const iconSize = React.useMemo(() => (size === 'small' ? '1rem' : '1.1rem'));
  const fontColor = React.useMemo(() =>
    color === 'sand' ? theme.colors.green : theme.colors.sand
  );
  const backgroundColor = React.useMemo(() =>
    color === 'sand' ? theme.colors.sand : theme.colors.green
  );

  React.useEffect(() => {
    if (showText && shrink) {
      const timeoutId = setTimeout(() => {
        setShowText(false);
      }, 6000);
      return () => clearTimeout(timeoutId);
    }
  }, [showText, shrink]);

  React.useEffect(() => {
    if (initialValue != null) {
      setShowText(initialValue);
    }
  }, [initialValue]);

  if (!content) {
    return null;
  }

  const { IconComponent, label } = content;

  return (
    <div
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onTouchStart={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setShowText(true);
      }}
      onMouseEnter={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setShowText(true);
      }}
      onMouseLeave={() => shrink && setShowText(false)}
      css={{
        fontSize,
        color: fontColor,
        lineHeight: '1rem',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        ...theme.fonts.futura,
      }}
      {...{ className }}
    >
      <div
        css={{
          width: theme.spacing(height),
          height: theme.spacing(height),
          backgroundColor,
          borderRadius: theme.spacing(height / 2),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: iconSize,
          zIndex: 1,
        }}
      >
        <IconComponent />
      </div>
      <div
        css={{
          overflow: 'hidden',
          height: theme.spacing(height),
          marginLeft: theme.spacing(-height / 2),
          fontSize,
        }}
      >
        <div
          css={{
            height: theme.spacing(height),
            fontSize,
            transform: `translateX(${showText ? 0 : '-100%'})`,
            transition: '0.6s',
            borderTopRightRadius: theme.spacing(height / 2),
            borderBottomRightRadius: theme.spacing(height / 2),
            backgroundColor,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingLeft: theme.spacing(height / 2),
            paddingRight: theme.spacing(2),
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

ProductLabel.defaultProps = {
  shrink: false,
  size: 'medium',
  color: 'sand',
  className: '',
};

export default ProductLabel;
